/*
===============================================================================================================
                                 Copyright 2022-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useState, useContext, useEffect, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import Tick from '@/public/icons/tick.svg';
import { ThemeContext } from '@/contexts/theme-context';
import { LocaleContext } from '@/contexts/locale-context';
import TextDiv from '@/components/text-div';
import ErrorReportingHOC from '@/components/error-reporting-hoc';

/**
 * Checkbox
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {JSX.Element} children - Standard React children. Content displayed next to box
 * @param {boolean} required - Whether or not this is a required field
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {string} defaultErrorMessage - Optional. Display this error on component load
 * @param {boolean} defaultSelected - Optional, default false. Does the box start as selected
 * @param {function} onchange - Optional, called when the state changes
 */
const Checkbox = forwardRef(({
  children,
  inputSelectorId,
  defaultSelected,
  setContent,
  onChange,
  ariaLabel,
  error, // Passed in automatically, no need to set
}, ref) => {
  const { textColor, primaryColor } = useContext(ThemeContext);
  const { textDirection } = useContext(LocaleContext);
  const [checked, setChecked] = useState(defaultSelected);

  useEffect(() => {
    setChecked(defaultSelected);
    setContent(defaultSelected ? 'checked' : null);
  }, [defaultSelected]);

  const onClicked = () => {
    const isNowChecked = !checked;
    setChecked(isNowChecked);
    setContent(isNowChecked ? 'checked' : null);
    if (onChange) onChange(isNowChecked);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      onClicked();
    }
  };

  return (
    <div id={inputSelectorId} className="checkbox-wrapper" dir={textDirection} ref={ref}>
      <span
        aria-label={ariaLabel}
        id={`${inputSelectorId}-checkbox-span`}
        className={error ? 'checkbox checkbox-error' : 'checkbox'}
        onClick={onClicked}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={`tick${checked ? ' visible' : ''}`}><Tick /></div>
      </span>
      <span className="checkbox-label">{children}</span>
      <div className={`error${error ? ' visible' : ''}${textDirection === 'rtl' ? ' rtl' : ''}`}>
        {error && <TextDiv>{error}</TextDiv>}
      </div>

      <style jsx>
        {`
          .checkbox-wrapper {
            width: 100%;
          }

          .checkbox {
            align-items: center;
            background-color: var(--color-white);
            border: 1px solid var(--color-mid-grey);
            border-radius: var(--border-radius);
            cursor: pointer;
            display: inline-flex;
            height: 40px;
            position: relative;
            vertical-align: middle;
            width: 40px;
          }

          .checkbox-error {
            border: 1px solid var(--color-error-red);
          }
          
          .checkbox:hover {
            border-color: ${primaryColor};
          }

          .tick {
            align-items: center;
            display: inline-flex;
            height: 24px;
            margin: 6px;
            vertical-align: middle;
            visibility: hidden;
            width: 24px;
          }

          .tick.visible {
            visibility: visible;
          }

          .checkbox-label {
            align-items: center;
            color: ${textColor};
            font-size: var(--font-size-button);
            line-height: 24px;
            margin: 0 16px;
            vertical-align: middle;
          }

          .error {
            visibility: hidden;
            margin-top: 3px;
            white-space: break-spaces;
          }

          .error.visible {
            visibility: visible;
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 8px;
          }

          .error.rtl {
            text-align: right;
          }

          @media only screen and (max-width: 768px) {
            .checkbox-label {
              font-size: var(--font-size-checkbox-mobile);
              vertical-align: none;
              align-items: none;
            }

          }
          @media only screen and (max-width: 320px) {
            .checkbox-label {
              font-size: var(--font-size-body-mobile);
            }
          }
        `}
      </style>
    </div>
  );
});

Checkbox.defaultProps = {
  inputSelectorId: null,
  children: null,
  defaultSelected: false,
  error: null,
  onChange: null,
  ariaLabel: null,
};

Checkbox.propTypes = {
  inputSelectorId: PropTypes.string,
  children: PropTypes.node,
  defaultSelected: PropTypes.bool,
  setContent: PropTypes.func.isRequired,
  error: PropTypes.string,
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default ErrorReportingHOC(Checkbox);
